import React, { useEffect, useState } from 'react';
import { BirdzNotif, BirdzTitle, useNotif } from '@applications-terrains/birdz-react-library';
import { Box, Button, Grid, IconButton, TextField } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import axios from 'axios';
import { Form, Formik, FormikValues } from 'formik';
import {
  useGetCompanies,
  useGetFP2EBrandsDropDown,
  useGetFP2EDiametersDropDown
} from '../../../../hooks/datarefs';
import {
  ModalWithLoaderAction,
  RequestStatus
} from '../../../Tools/ModalWithLoader/ModalWithLoader';
import { useTranslation } from 'react-i18next';
import SelectWithSearchModal from '../../../Tools/Selects/SelectWithSearchModal';

export default function FP2EAdditionalConfigForm({
  endpoint,
  setRequestStatus,
  action,
  setAction,
  id
}: {
  endpoint: string;
  setRequestStatus: (value: RequestStatus) => void;
  action: ModalWithLoaderAction;
  setAction: (value: ModalWithLoaderAction) => void;
  id: number | null;
}) {
  const { t } = useTranslation();
  const { notif, notifOptions } = useNotif();
  const [values, setValues] = useState<FormikValues>();
  const [isError, setIsError] = useState<boolean>();
  const initialValues = {
    BrandId: null,
    ModelId: null,
    DiameterId: null,
    MeterKey: null,
    companies: []
  };
  const [modelOptions, setModelOptions] = useState<{ label: string; value: number }[]>([]);
  const { data: brandsList } = useGetFP2EBrandsDropDown();
  const { data: diametersList } = useGetFP2EDiametersDropDown();
  const { data: clients } = useGetCompanies();

  const fetchModelOptions = (brandId: number) => {
    axios
      .get(`/api/bconnect/webapp/administrator/references/fp2e-sensor-brands/${brandId}/`)
      .then((response) => {
        const models = response.data.Models ?? [];
        setModelOptions(
          models.map((m: any) => ({ label: `${m.Label} (Code ${m.Code})`, value: m.Id }))
        );
      });
  };

  useEffect(() => {
    if (action === 'edit' && id !== null) {
      axios.get(endpoint + `${id}/`).then((response) => {
        setValues(response.data);
        if (response.data.BrandId) {
          fetchModelOptions(response.data.BrandId);
        }
      });
    }
  }, [action, id]);

  const handleSubmit = async (values: FormikValues) => {
    const payload = values;

    if (!id) {
      await axios
        .post(endpoint, payload)
        .then(() => {
          setRequestStatus('success');
          setTimeout(() => {
            setRequestStatus(null);
            setAction(null);
          }, 1500);
        })
        .catch(() => {
          notif({
            content: t('anErrorOccurred'),
            type: 'error'
          });
        });
    } else {
      await axios
        .put(endpoint + `${id}`, payload)
        .then(() => {
          setRequestStatus('success');
          setTimeout(() => {
            setRequestStatus(null);
            setAction(null);
          }, 1500);
        })
        .catch(() => {
          notif({
            content: t('anErrorOccurred'),
            type: 'error'
          });
        });
    }
  };

  return (
    <>
      <BirdzTitle>{t('referencesTables.fp2e.additionalConfiguration')}</BirdzTitle>
      <Formik
        initialValues={values ? values : initialValues}
        onSubmit={handleSubmit}
        validateOnChange={true}
        validateOnBlur={true}
        enableReinitialize={true}
      >
        {({ values, setFieldValue }) => {
          const needRequiredFields =
            !values.BrandId || !values.ModelId || !values.DiameterId || !values.MeterKey;
          setIsError(needRequiredFields);
          return (
            <Form>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <SelectWithSearchModal
                    label={t('referencesTables.brand') + '*'}
                    options={brandsList ?? []}
                    value={values.BrandId}
                    onChange={(val) => {
                      setFieldValue('BrandId', val);
                      setFieldValue('ModelId', null);
                      if (val) {
                        fetchModelOptions(val);
                      } else {
                        setModelOptions([]);
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <SelectWithSearchModal
                    label={t('referencesTables.model') + '*'}
                    options={modelOptions}
                    value={values.ModelId}
                    onChange={(val) => setFieldValue('ModelId', val)}
                    disabled={!values.BrandId}
                  />
                </Grid>

                <Grid item xs={12}>
                  <SelectWithSearchModal
                    label={t('referencesTables.diameter') + '*'}
                    options={diametersList ?? []}
                    value={values.DiameterId}
                    onChange={(val) => setFieldValue('DiameterId', val)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      size="small"
                      type="number"
                      name="MeterKey"
                      value={values.MeterKey || ''}
                      fullWidth
                      label={t('referencesTables.meterKey') + '*'}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => {
                        const value = e.target.value;

                        // Accepte seulement les entiers positifs
                        if (value === '' || (/^\d+$/.test(value) && parseInt(value) > 0)) {
                          setFieldValue('MeterKey', value);
                        }
                      }}
                      onKeyDown={(e) => {
                        // Bloque les touches: -, +, e, E, .  ,
                        if (['-', '+', 'e', 'E', '.', ','].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      inputProps={{
                        min: 1,
                        step: 1
                      }}
                    />
                    {values.MeterKey && (
                      <IconButton
                        size="small"
                        onClick={() => setFieldValue('MeterKey', null)}
                        sx={{
                          position: 'absolute',
                          right: 32,
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <SelectWithSearchModal
                    multiple
                    label={t('referencesTables.clients')}
                    options={(clients ?? []).map((c) => ({ label: c.Name, value: c.Id }))}
                    value={values.companies ?? []}
                    onChange={(val) => setFieldValue('companies', val)}
                  />
                </Grid>
              </Grid>

              <Grid container justifyContent="center" sx={{ mt: 2 }}>
                <Grid item>
                  <Button
                    sx={{ ml: 2 }}
                    onClick={() => {
                      setRequestStatus(null);
                      setAction(null);
                    }}
                  >
                    {t('referencesTables.cancel')}
                  </Button>
                  <Button type="submit" variant="contained" disabled={isError}>
                    {action === 'edit' ? t('referencesTables.edit') : t('referencesTables.add')}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>

      <BirdzNotif options={notifOptions} />
    </>
  );
}
